import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Skeleton} from 'src/design/atoms/Skeleton';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';

import type {
  FormDefinitionDependenciesResponse,
  FormDefinitionDependencyItem,
  FormDefinitionResponse,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormBuilderContent} from 'src/app/form-builder/components/FormBuilderContent';
import {
  SectionValidationProvider,
  useSectionValidation,
} from 'src/shared/hooks/SectionValidationContext';
import {
  useGetFieldList,
  useGetFormSettings,
  useLoadFormSettings,
  useResetFormBuilderState,
  useUpdateFieldList,
} from 'src/app/form-builder/hooks/useFormBuilder';
import {FormBuilderProvider} from 'src/app/form-builder/context/FormBuilderContext';
import {formBuilderRegistry} from 'src/app/form-builder/formBuilderRegistry';
import {SidebarLinkForms} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteFormDefinitionMutation,
  useGetFormDefinitionFlattenedQuery,
  useLazyGetFormDefinitionDependenciesQuery,
  useUpdateFormDefinitionMutation,
} from 'src/redux/queries/formDefinition';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useFormsBreadcrumbs} from 'src/shared/hooks/useFormsBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  mapFormDefinitionResponseToFormSettingsV2,
  mapFormSettingsV2ToFormDefinitionRequest,
} from 'src/types/FormV2Mapping';
import {mapFormDefinitionResponseToFormFieldsV2} from 'src/app/forms/formHelpers';
import {
  FormBuilderDocumentTitle,
  buildSectionValidationMessage,
} from '../formBuilderHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

interface DependencyListProps {
  title: string;
  items: FormDefinitionDependencyItem[];
}

const DependencyList: React.FC<DependencyListProps> = ({title, items}) => {
  if (items.length === 0) return null;

  return (
    <div>
      <Text size="sm" fw={600}>
        {title}:
      </Text>
      <List size="sm" withPadding>
        {items.map(item => (
          <List.Item key={item.id}>{item.name}</List.Item>
        ))}
      </List>
    </div>
  );
};

const ViewFormBuilderPageContent = () => {
  const {formId} = useParams();
  const navigate = useAppNavigate();
  const formSettings = useGetFormSettings();
  const formFields = useGetFieldList();
  const {validateAll} = useSectionValidation();

  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);
  const [confirmArchiveOpened, confirmArchiveHandlers] = useDisclosure(false);
  const [dependencies, setDependencies] =
    useState<FormDefinitionDependenciesResponse | null>(null);
  const [isDependenciesLoading, setIsDependenciesLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const perms = useModulePermissions(ModuleEnum.Forms);

  const formIdNumber = useMemo(
    () => (formId ? parseInt(formId, 10) : null),
    [formId]
  );

  const loadFormSettings = useLoadFormSettings();
  const updateFieldList = useUpdateFieldList();
  const resetFormBuilderState = useResetFormBuilderState();

  const [updateFormDefinition] = useUpdateFormDefinitionMutation();
  const [deleteFormDefinition] = useDeleteFormDefinitionMutation();

  // Stable reference to prevent re-initialization on re-renders
  const loadFormSettingsRef = React.useRef(loadFormSettings);
  const updateFieldListRef = React.useRef(updateFieldList);

  React.useEffect(() => {
    loadFormSettingsRef.current = loadFormSettings;
    updateFieldListRef.current = updateFieldList;
  });

  React.useEffect(() => {
    resetFormBuilderState();
  }, []);

  const handleFormDefinitionResponse = React.useCallback(
    (response: FormDefinitionResponse) => {
      const mappedSettings =
        mapFormDefinitionResponseToFormSettingsV2(response);
      const mappedFields = mapFormDefinitionResponseToFormFieldsV2(
        response,
        true
      );

      loadFormSettingsRef.current(mappedSettings);
      updateFieldListRef.current(mappedFields);
    },
    []
  );

  const {
    data: flattenedData,
    isLoading,
    error: fetchError,
  } = useGetFormDefinitionFlattenedQuery(
    {id: formIdNumber!, flattenSubform: false},
    {skip: formIdNumber === null}
  );

  React.useEffect(() => {
    if (flattenedData && !initialized) {
      handleFormDefinitionResponse(flattenedData);
      setInitialized(true);
    }
  }, [flattenedData, initialized, handleFormDefinitionResponse]);

  React.useEffect(() => {
    if (fetchError) {
      setAlert(parseErrorMessage(fetchError), 'error');
    }
  }, [fetchError, setAlert]);

  // Fetch dependencies on demand for the archive modal
  const [fetchDependencies] = useLazyGetFormDefinitionDependenciesQuery();

  const handleSubmit = React.useCallback(async () => {
    if (!formIdNumber) return;

    const results = await validateAll();
    const message = buildSectionValidationMessage(results);
    if (message) {
      setAlert(message, 'error');
      return;
    }

    try {
      setIsSavePending(true);

      const body = mapFormSettingsV2ToFormDefinitionRequest(
        formIdNumber,
        parseInt(formSettings.version ?? '0', 10),
        formSettings,
        formFields
      );
      const response = await updateFormDefinition(body).unwrap();

      setIsSavePending(false);
      setAlert('Form was successfully updated', 'success');

      handleFormDefinitionResponse(response);
    } catch (error) {
      setIsSavePending(false);
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [
    validateAll,
    formIdNumber,
    formSettings,
    formFields,
    setAlert,
    handleFormDefinitionResponse,
    updateFormDefinition,
  ]);

  const handleOpenArchiveModal = React.useCallback(async () => {
    if (!formIdNumber) return;

    setIsDependenciesLoading(true);
    setDependencies(null);
    confirmArchiveHandlers.open();

    try {
      const result = await fetchDependencies(formIdNumber);
      if (result.data) {
        setDependencies(result.data);
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      confirmArchiveHandlers.close();
    }
    setIsDependenciesLoading(false);
  }, [formIdNumber, setAlert, confirmArchiveHandlers, fetchDependencies]);

  const handleArchive = React.useCallback(async () => {
    if (!formIdNumber) return;

    confirmArchiveHandlers.close();

    try {
      setIsSavePending(true);
      await deleteFormDefinition(formIdNumber).unwrap();

      setAlert('Form was successfully archived', 'success');
      navigate(PathUtility.FormsDashboard('manage'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  }, [
    formIdNumber,
    setAlert,
    navigate,
    confirmArchiveHandlers,
    deleteFormDefinition,
  ]);

  const renderArchiveModalBody = () => {
    if (isDependenciesLoading) {
      return <Text size="sm">Loading dependencies...</Text>;
    }

    if (dependencies?.hasDependencies) {
      return (
        <Stack>
          <Text size="sm" c="red">
            This form cannot be archived because it has active dependencies:
          </Text>
          <DependencyList
            title="Notification Rules"
            items={dependencies.notificationRules}
          />
          <DependencyList
            title="Workflow Definitions"
            items={dependencies.workflowDefinitions}
          />
          <DependencyList
            title="Parent Forms (using as subform)"
            items={dependencies.parentForms}
          />
          <Text size="sm">
            Please archive or modify these items before archiving this form.
          </Text>
        </Stack>
      );
    }

    return (
      <Text size="sm">
        The form will be archived and no longer be accessible. Form submissions
        will no longer be accessible.
      </Text>
    );
  };

  const {goBack: handleCancel} = useBackNavigation(
    PathUtility.FormsDashboard('manage')
  );

  const {breadcrumbs, fallbackTo} = useFormsBreadcrumbs({
    mode: 'form-builder-edit',
    formDefinitionName: formSettings.name,
  });

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
        <Stack gap="lg">
          <Skeleton height={20} width="30%" />
          <Skeleton height={40} width="50%" />
          <Grid gutter="md">
            <Grid.Col span={{base: 12, lg: 3}}>
              <Skeleton height={300} />
            </Grid.Col>
            <Grid.Col span={{base: 12, lg: 3}}>
              <Skeleton height={300} />
            </Grid.Col>
            <Grid.Col span={{base: 12, lg: 6}}>
              <Skeleton height={300} />
            </Grid.Col>
          </Grid>
        </Stack>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
      <Stack gap="lg">
        <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
        <PageHeader>
          <PageHeader.Title icon={SidebarLinkForms.icon}>
            {formSettings.name || 'Edit Form'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            {perms.update && (
              <PageHeader.Action
                variant="save"
                onClick={handleSubmit}
                loading={isSavePending}>
                Save
              </PageHeader.Action>
            )}
            {perms.archive && (
              <PageHeader.Action
                variant="delete"
                onClick={handleOpenArchiveModal}>
                Archive
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <FormBuilderContent registry={formBuilderRegistry} />

        {perms.update && (
          <Group>
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            <PageHeader.Action
              variant="save"
              onClick={handleSubmit}
              loading={isSavePending}>
              Save
            </PageHeader.Action>
          </Group>
        )}
      </Stack>

      <ConfirmDialog
        opened={confirmArchiveOpened}
        onClose={() => confirmArchiveHandlers.close()}
        onConfirm={handleArchive}
        confirmationButtonColor="red"
        confirmationButtonDisabled={
          isDependenciesLoading || (dependencies?.hasDependencies ?? false)
        }
        title="Are you sure you want to archive this form?"
        body={renderArchiveModalBody()}
      />
    </ContentWrapper>
  );
};

export const ViewFormBuilderPage = () => {
  useSetDocumentTitle(FormBuilderDocumentTitle);

  return (
    <FormBuilderProvider>
      <SectionValidationProvider>
        <ViewFormBuilderPageContent />
      </SectionValidationProvider>
    </FormBuilderProvider>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.FormBuilderView(':formId'),
  element: <ViewFormBuilderPage />,
  module: ModuleEnum.Forms,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
